import React, { useState } from 'react';
import { getEntryCodes } from '../../lib/helpers';
import { usePhotoUrlList } from '../../hooks/usePhotoUrl';
import { EntrySummaryRow } from './EntrySummaryRow';

const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv)/i;

/**
 * One evidence entry with its review controls — used by the trainer's combined
 * Evidence page and by the employer/supervisor review queue, so the rules about
 * comments stay identical in both.
 *
 * Comments: required when returning an entry (an apprentice can't act on "no"
 * with no reason), optional when approving, and editable afterwards either way.
 */
export function ReviewCard({
  entry, apprenticeName, getUnitName, readOnly, collapsible = false, defaultOpen = false,
  onApprove, onReject, onUpdateNote, onEdit, onLightbox, onPDF,
}) {
  const [note, setNote] = useState(entry.review_note || '');
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [open, setOpen] = useState(!collapsible || defaultOpen);

  const codes = getEntryCodes(entry);
  const status = entry.status || 'pending';

  const rawPhotoList = (() => {
    if (entry.photo_urls) {
      try { return JSON.parse(entry.photo_urls); } catch {}
    }
    return [entry.photo_fault_url, entry.photo_repair_url, entry.photo_completed_url, entry.photo_additional_url].filter(Boolean);
  })();
  const photoUrls = usePhotoUrlList(rawPhotoList);

  const noteChanged = (note || '') !== (entry.review_note || '');

  async function run(action, { requireNote = false, done = '' } = {}) {
    if (requireNote && !note.trim()) {
      return setErrMsg('Please add a comment so the apprentice knows what to fix.');
    }
    setSaving(true);
    setErrMsg('');
    setSavedMsg('');
    try {
      await action();
      if (done) setSavedMsg(done);
    } catch (e) {
      setErrMsg(e?.message || 'That didn\'t save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const hasFaultReason = !!entry.fault_reason;

  if (collapsible && !open) {
    return (
      <div className="ev-card" style={{ marginBottom: 8 }}>
        <EntrySummaryRow
          entry={entry}
          codes={codes}
          apprenticeName={apprenticeName}
          photoCount={rawPhotoList.length}
          open={false}
          onToggle={() => setOpen(true)}
        />
      </div>
    );
  }

  return (
    <div className="ev-card">
      {collapsible ? (
        <EntrySummaryRow
          entry={entry}
          codes={codes}
          apprenticeName={apprenticeName}
          photoCount={rawPhotoList.length}
          open
          onToggle={() => setOpen(false)}
        />
      ) : (
        <div className="ev-hdr">
          <div>
            <div className="ev-unit">{codes.join(', ') || entry.unit_code || '—'}</div>
            <div className="ev-unitname">{codes.map(c => getUnitName(c)).join(' · ')}</div>
            <div className="ev-meta">
              {apprenticeName && <span style={{ color: 'var(--head)', fontWeight: 600 }}>{apprenticeName} · </span>}
              Job #{entry.job_number}
              {entry.vehicle_rego && <span> · {entry.vehicle_rego}</span>}
              {' · '}{entry.date}
            </div>
          </div>
          <span className={`badge ${status}`}>{status}</span>
        </div>
      )}

      <div className="ev-body">
        {collapsible && (
          <div className="ev-unitname" style={{ marginBottom: 10 }}>
            {codes.map(c => getUnitName(c)).join(' · ')}
          </div>
        )}
        {hasFaultReason ? (
          <>
            <div className="ev-field-label">Fault / Reason</div>
            <div className="ev-field-val">{entry.fault_reason}</div>
            <div className="ev-field-label">What did they do?</div>
            <div className="ev-field-val">{entry.what_doing || '—'}</div>
            {entry.tools_used && (
              <>
                <div className="ev-field-label">Tools / Equipment</div>
                <div className="ev-field-val">{entry.tools_used}</div>
              </>
            )}
            <div className="ev-field-label">Outcome</div>
            <div className="ev-field-val">{entry.outcome || entry.why_doing || '—'}</div>
          </>
        ) : (
          <>
            <div className="ev-field-label">What are they doing?</div>
            <div className="ev-field-val">{entry.what_doing || '—'}</div>
            <div className="ev-field-label">Why are they doing it?</div>
            <div className="ev-field-val">{entry.why_doing || '—'}</div>
          </>
        )}

        {rawPhotoList.length > 0 && (
          <div className="ev-field-label" style={{ marginTop: 10 }}>
            {rawPhotoList.length} photo{rawPhotoList.length === 1 ? '' : 's'} / video{rawPhotoList.length === 1 ? '' : 's'} attached
          </div>
        )}

        {photoUrls.length > 0 && (
          <div className="ev-photos">
            {photoUrls.map((src, i) => {
              if (!src) return null;
              const isVid = VIDEO_RE.test(rawPhotoList[i] || '');
              if (isVid) {
                return (
                  <div key={i} style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }} onClick={() => onLightbox(src)} title={`Video ${i + 1}`}>
                    <video src={src} className="ev-photo" muted style={{ objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)', borderRadius: 4 }}>
                      <span style={{ fontSize: 20, color: '#fff' }}>▶</span>
                    </div>
                  </div>
                );
              }
              return <img key={i} src={src} className="ev-photo" title={`Photo ${i + 1}`} onClick={() => onLightbox(src)} alt={`Photo ${i + 1}`} />;
            })}
          </div>
        )}

        {/* The comment currently sitting on the entry, for context */}
        {entry.review_note && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 4, fontSize: 12, color: 'var(--text)',
            background: status === 'rejected' ? 'rgba(192,57,43,.08)' : 'rgba(39,174,96,.06)',
            border: status === 'rejected' ? '1px solid rgba(192,57,43,.3)' : '1px solid rgba(39,174,96,.25)',
          }}>
            <strong style={{ color: status === 'rejected' ? 'var(--red)' : 'var(--green)' }}>
              {status === 'rejected' ? 'Current feedback to apprentice:' : 'Comment on this entry:'}
            </strong>{' '}
            {entry.review_note}
          </div>
        )}

        {!readOnly && (
          <>
            <label className="flbl" style={{ marginTop: 12, display: 'block' }}>
              {status === 'approved' ? 'Comment for the apprentice (optional)'
                : status === 'rejected' ? 'What needs fixing (required)'
                : 'Comment — required if you return this entry'}
            </label>
            <textarea
              className="ftxt"
              rows={2}
              placeholder={status === 'approved'
                ? 'e.g. Good detail on the torque specs — keep doing that.'
                : 'e.g. Need a photo of the worn pads before replacement, and your face in at least one shot.'}
              value={note}
              onChange={ev => { setNote(ev.target.value); setSavedMsg(''); setErrMsg(''); }}
            />

            <div className="ev-actions" style={{ marginTop: 10 }}>
              {status === 'pending' && (
                <>
                  <button className="btn xs green-outline" disabled={saving}
                    onClick={() => run(() => onApprove(entry.id, note.trim() || null), { done: 'Approved.' })}>
                    ✓ Approve
                  </button>
                  <button className="btn xs danger-outline" disabled={saving}
                    onClick={() => run(() => onReject(entry.id, note.trim()), { requireNote: true, done: 'Sent back.' })}>
                    ✗ Request Changes
                  </button>
                </>
              )}

              {status === 'rejected' && (
                <>
                  <button className="btn xs green-outline" disabled={saving}
                    onClick={() => run(() => onApprove(entry.id, note.trim() || null), { done: 'Approved.' })}>
                    ✓ Approve Anyway
                  </button>
                  <button className="btn xs outline" disabled={saving || !noteChanged}
                    onClick={() => run(() => onUpdateNote(entry.id, note.trim()), { requireNote: true, done: 'Feedback updated.' })}>
                    ✎ Update Feedback
                  </button>
                </>
              )}

              {status === 'approved' && (
                <>
                  <button className="btn xs outline" disabled={saving || !noteChanged}
                    onClick={() => run(() => onUpdateNote(entry.id, note.trim() || null), { done: 'Comment saved.' })}>
                    ✎ {entry.review_note ? 'Update Comment' : 'Add Comment'}
                  </button>
                  <button className="btn xs danger-outline" disabled={saving}
                    onClick={() => run(() => onReject(entry.id, note.trim()), { requireNote: true, done: 'Sent back for changes.' })}>
                    ↩ Return for Changes
                  </button>
                </>
              )}

              {onEdit && (
                <button className="btn xs outline" disabled={saving} onClick={() => onEdit(entry)}>✏️ Edit entry</button>
              )}
              {onPDF && <button className="btn xs outline" onClick={() => onPDF(entry)}>⬇ PDF</button>}
              {saving && <div className="spin" style={{ width: 16, height: 16, borderWidth: 2 }} />}
            </div>

            {errMsg && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(192,57,43,.12)', color: '#ffb0b0', borderRadius: 4, fontSize: 12.5 }}>
                {errMsg}
              </div>
            )}
            {savedMsg && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(39,174,96,.12)', color: 'var(--green)', borderRadius: 4, fontSize: 12.5 }}>
                {savedMsg}
              </div>
            )}
          </>
        )}

        {readOnly && (onPDF || onEdit) && (
          <div className="ev-actions" style={{ marginTop: 12 }}>
            {onEdit && <button className="btn xs outline" onClick={() => onEdit(entry)}>✏️ Edit entry</button>}
            {onPDF && <button className="btn xs outline" onClick={() => onPDF(entry)}>⬇ PDF</button>}
          </div>
        )}
      </div>
    </div>
  );
}
